import React from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { styled } from 'dripsy';
import { View } from 'react-native';

import { Loader } from '@components';
import {
  AppNavigatorNavigationPropType,
  AppNavigatorRoutePropType,
  screens,
} from '@navigation';
import { parseApiError } from '@utils';
import { AnalyticsService, ToastService } from '@services';
import {
  usePaymentMethod,
  useAsyncInit,
  useNavOptionsSet,
} from '@hooks';
import { ConfirmPaymentTypeEnum } from '@constants';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { PaymentMethodError } from '@errors';

const Container = styled(View)({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  bg: '$background0',
});

type NavigationPropType =
  AppNavigatorNavigationPropType<'PaymentConfirm'>;
type RoutePropType = AppNavigatorRoutePropType<'PaymentConfirm'>;

export const PaymentConfirmScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const paymentMethod = usePaymentMethod();

  useNavOptionsSet({
    title: t('paymentConfirm.title'),
  });

  useAsyncInit(async () => {
    if (!paymentMethod.isLoaded) {
      return;
    }

    switch (route.params.type) {
      case ConfirmPaymentTypeEnum.Method:
        confirmMethod();
        break;
      case ConfirmPaymentTypeEnum.Subscription:
        confirmSignUpSubscriptionPurchase();
        break;
      case ConfirmPaymentTypeEnum.Credits:
        confirmPayment();
        break;
      default:
        break;
    }
  }, [paymentMethod.isLoaded]);

  async function confirmMethod() {
    try {
      if (route.params.type !== ConfirmPaymentTypeEnum.Method) {
        return;
      }

      const setupIntent = await paymentMethod.retrieveSetupIntent(
        route.params.setup_intent_client_secret,
      );

      if (!setupIntent) {
        // This case should never happened
        throw new Error();
      }

      if (setupIntent.status !== 'succeeded') {
        if (setupIntent.last_setup_error) {
          throw new PaymentMethodError(setupIntent.last_setup_error);
        } else {
          throw new Error();
        }
      }

      await viewer.paymentMethodList.confirmPaymentMethod.run({
        setupIntentId: setupIntent.id,
      });

      ToastService.showSuccess(
        t('paymentConfirm.successMessageMethod'),
      );
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    } finally {
      nav.replace(screens.SwitchNavigator, {
        screen: screens.MainNavigator,
        params: {
          screen: screens.ProfileNavigator,
          params: { screen: screens.CreditCards },
        },
      });
    }
  }

  async function confirmSignUpSubscriptionPurchase() {
    try {
      if (route.params.type !== ConfirmPaymentTypeEnum.Subscription) {
        return;
      }

      const paymentIntent = await paymentMethod.retrievePaymentIntent(
        route.params.payment_intent_client_secret,
      );

      if (!paymentIntent) {
        // This case should never happened
        throw new Error();
      }

      if (paymentIntent.status !== 'succeeded') {
        if (paymentIntent.last_payment_error) {
          throw new PaymentMethodError(
            paymentIntent.last_payment_error,
          );
        } else {
          throw new Error();
        }
      }

      const signUpConfirmRes =
        await viewer.confirmSubscriptionPayment.run({
          paymentIntentId: paymentIntent.id,
        });

      if (!signUpConfirmRes) {
        // This case should never happened
        throw new Error();
      }

      // In paymentIntent we have amount in cents, (amount 100 is 1$)
      const formattedAmount = paymentIntent.amount / 100;

      AnalyticsService.trackPurchase({
        currency: paymentIntent.currency,
        value: formattedAmount,
        eventId: signUpConfirmRes.conversionEventId,
        code: route.params.tariffPlan,
      });

      nav.replace(screens.SwitchNavigator, {
        screen: screens.AuthNavigator,
        params: { screen: screens.SignIn },
      });
      ToastService.showSuccess(
        t('paymentConfirm.successMessageSubscription'),
      );
    } catch (error) {
      nav.replace(screens.SwitchNavigator, {
        screen: screens.AuthNavigator,
        params: { screen: screens.SignUp },
      });
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function confirmPayment() {
    try {
      if (route.params.type !== ConfirmPaymentTypeEnum.Credits) {
        return;
      }

      const paymentIntent = await paymentMethod.retrievePaymentIntent(
        route.params.payment_intent_client_secret,
      );

      if (!paymentIntent) {
        // This case should never happened
        throw new Error();
      }

      if (paymentIntent.status !== 'succeeded') {
        if (paymentIntent.last_payment_error) {
          throw new PaymentMethodError(
            paymentIntent.last_payment_error,
          );
        } else {
          throw new Error();
        }
      }

      await viewer.confirmPayment.run({
        paymentIntentId: paymentIntent.id,
      });

      ToastService.showSuccess(
        t('paymentConfirm.successMessageCredits'),
      );
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    } finally {
      nav.replace(screens.SwitchNavigator, {
        screen: screens.MainNavigator,
        params: {
          screen: screens.TabNavigator,
          params: { screen: screens.TrackFeedback },
        },
      });
    }
  }

  return (
    <Container>
      <Loader size="large" />
    </Container>
  );
});
